/**
 * Expande placeholders {{VAR}} de modelos de texto na emissão/réplica.
 * Tipos: Texto | Data | Valor | Formula
 *
 * ConfigJson (Data): { "regra": "mesmoDiaProximoMes" | "hoje" | "fix", "formato": "dd/MM/yyyy" }
 * ConfigJson (Formula): { "expressao": "<VALORSERVICO>*0.06" } — tokens: VALORSERVICO, VALORLIQUIDO, ALIQUOTAISS, DESCONTO
 */

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatDateBr(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function addMonthsSameDay(date, months) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  // Se o dia estourou (ex.: 31→fev), usa último dia do mês
  if (d.getDate() < day) d.setDate(0);
  return d;
}

function parseConfig(configJson) {
  if (!configJson) return {};
  if (typeof configJson === 'object') return configJson;
  try {
    return JSON.parse(configJson) || {};
  } catch {
    return {};
  }
}

function formatMoneyBr(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function resolveData(variavel, valoresManuais) {
  const nome = (variavel.nome || '').toUpperCase();
  const manual = valoresManuais?.[nome] ?? valoresManuais?.[variavel.nome];
  if (manual != null && String(manual).trim() !== '') {
    // Se já veio formatado, mantém; se ISO/date, formata
    const raw = String(manual).trim();
    const asDate = new Date(raw);
    if (!Number.isNaN(asDate.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return formatDateBr(asDate);
    }
    return raw;
  }

  const cfg = parseConfig(variavel.configJson);
  const regra = cfg.regra || (variavel.valorPadrao === '+1mes' ? 'mesmoDiaProximoMes' : 'hoje');
  let d = new Date();
  if (regra === 'mesmoDiaProximoMes') d = addMonthsSameDay(new Date(), 1);
  else if (regra === 'fix' && cfg.data) d = new Date(cfg.data);
  else if (variavel.valorPadrao && /^\d{4}-\d{2}-\d{2}/.test(variavel.valorPadrao)) {
    d = new Date(variavel.valorPadrao);
  }
  return formatDateBr(d);
}

function resolveValor(variavel, valoresManuais, contexto) {
  const nome = (variavel.nome || '').toUpperCase();
  const manual = valoresManuais?.[nome] ?? valoresManuais?.[variavel.nome];
  if (manual != null && String(manual).trim() !== '') {
    const n = Number(String(manual).replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? formatMoneyBr(n) : String(manual);
  }
  if (variavel.valorPadrao != null && String(variavel.valorPadrao).trim() !== '') {
    const n = Number(String(variavel.valorPadrao).replace(',', '.'));
    return Number.isFinite(n) ? formatMoneyBr(n) : String(variavel.valorPadrao);
  }
  if (contexto?.valorServico != null) return formatMoneyBr(contexto.valorServico);
  return '';
}

function resolveFormula(variavel, valoresManuais, contexto) {
  const nome = (variavel.nome || '').toUpperCase();
  const manual = valoresManuais?.[nome] ?? valoresManuais?.[variavel.nome];
  if (manual != null && String(manual).trim() !== '') return String(manual);

  const cfg = parseConfig(variavel.configJson);
  let expr = cfg.expressao || variavel.valorPadrao || '';
  const map = {
    VALORSERVICO: Number(contexto?.valorServico) || 0,
    VALORLIQUIDO: Number(contexto?.valorLiquido ?? contexto?.valorServico) || 0,
    ALIQUOTAISS: Number(contexto?.aliquotaIss) || 0,
    DESCONTO: Number(contexto?.descontoIncondicionado) || 0,
  };
  Object.entries(map).forEach(([k, v]) => {
    expr = expr.replace(new RegExp(`<${k}>`, 'gi'), String(v));
    expr = expr.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'gi'), String(v));
    expr = expr.replace(new RegExp(k, 'gi'), String(v));
  });
  // Só números e operadores básicos
  if (!/^[\d.\s+\-*/()]+$/.test(expr)) return variavel.valorPadrao || '';
  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict"; return (${expr});`)();
    return Number.isFinite(result) ? formatMoneyBr(result) : String(result);
  } catch {
    return variavel.valorPadrao || '';
  }
}

function resolveTexto(variavel, valoresManuais) {
  const nome = (variavel.nome || '').toUpperCase();
  const manual = valoresManuais?.[nome] ?? valoresManuais?.[variavel.nome];
  if (manual != null && String(manual).trim() !== '') return String(manual);
  return variavel.valorPadrao != null ? String(variavel.valorPadrao) : '';
}

/**
 * @param {string} conteudo
 * @param {Array<{nome:string,tipo:string,valorPadrao?:string,configJson?:string}>} variaveis
 * @param {Record<string,string>} valoresManuais
 * @param {object} contexto campos da NF (valorServico, aliquotaIss, …)
 */
export function expandModeloTexto(conteudo, variaveis = [], valoresManuais = {}, contexto = {}) {
  let texto = conteudo || '';
  const vars = Array.isArray(variaveis) ? variaveis : [];

  vars.forEach((v) => {
    if (!v?.nome) return;
    const tipo = (v.tipo || 'Texto').toLowerCase();
    let valor = '';
    if (tipo === 'data') valor = resolveData(v, valoresManuais);
    else if (tipo === 'valor') valor = resolveValor(v, valoresManuais, contexto);
    else if (tipo === 'formula' || tipo === 'fórmula') valor = resolveFormula(v, valoresManuais, contexto);
    else valor = resolveTexto(v, valoresManuais);

    const re = new RegExp(`\\{\\{\\s*${v.nome}\\s*\\}\\}`, 'gi');
    texto = texto.replace(re, valor);
  });

  // Placeholders de contexto da NF sem variável declarada
  const ctxMap = {
    VALORSERVICO: contexto?.valorServico != null ? formatMoneyBr(contexto.valorServico) : '',
    VALORLIQUIDO: contexto?.valorLiquido != null ? formatMoneyBr(contexto.valorLiquido) : '',
    ALIQUOTAISS: contexto?.aliquotaIss != null ? String(contexto.aliquotaIss) : '',
    TOMADOR: contexto?.tomadorNome || '',
    CNPJTOMADOR: contexto?.tomadorDoc || '',
  };
  Object.entries(ctxMap).forEach(([k, v]) => {
    if (!v) return;
    texto = texto.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'gi'), v);
  });

  return texto;
}

export function listarPlaceholders(conteudo) {
  const matches = String(conteudo || '').matchAll(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g);
  return [...new Set([...matches].map((m) => m[1].toUpperCase()))];
}

export default expandModeloTexto;
